import React, { useEffect, useState } from "react";
import { intelligenceAdminApi } from "../../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Brain, Loader2, Search, ShieldCheck, School, Building2 } from "lucide-react";

export default function IntelligenceAccessPage({ session }) {
  const [access, setAccess] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState("");

  const loadAccess = () => intelligenceAdminApi.listAccess(session)
    .then((d) => setAccess(d.access || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { loadAccess(); }, []); // eslint-disable-line

  const search = async () => {
    setSearching(true);
    try { const d = await intelligenceAdminApi.candidates(q, session); setResults(d.candidates || []); }
    catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const toggle = async (scope_type, scope_id, enabled) => {
    const key = `${scope_type}:${scope_id}`;
    setBusy(key);
    try {
      await intelligenceAdminApi.setAccess(scope_type, scope_id, enabled, session);
      toast.success(enabled ? "School Intelligence enabled" : "Disabled");
      loadAccess();
    } catch { toast.error("Update failed"); }
    setBusy("");
  };

  const enabledKeys = new Set(access.filter((a) => a.enabled).map((a) => `${a.scope_type}:${a.scope_id}`));
  const Icon = (t) => (t === "portfolio" ? Building2 : School);

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="intel-access-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><Brain className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">School Intelligence™ Access</h1>
          <p className="text-sm text-foreground/50">Enable the AI advisor for specific schools and portfolios.</p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Enabled scopes</div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-foreground/40" /></div>
        ) : access.length === 0 ? (
          <div className="text-sm text-foreground/40 py-3">Nothing enabled yet. Search below to turn it on for a school or portfolio.</div>
        ) : (
          <div className="space-y-1" data-testid="intel-access-list">
            {access.map((a) => { const I = Icon(a.scope_type); return (
              <div key={`${a.scope_type}:${a.scope_id}`} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted">
                <div className="flex items-center gap-2 min-w-0">
                  <I className="h-4 w-4 text-foreground/40 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{a.name || a.scope_id}</div>
                    <div className="text-[11.5px] text-foreground/45 capitalize">{a.scope_type}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11.5px] font-semibold ${a.enabled ? "text-emerald-600" : "text-foreground/40"}`}>{a.enabled ? "Enabled" : "Disabled"}</span>
                  <Switch checked={!!a.enabled} disabled={busy === `${a.scope_type}:${a.scope_id}`}
                    onCheckedChange={(v) => toggle(a.scope_type, a.scope_id, v)} data-testid={`intel-switch-${a.scope_id}`} />
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3 flex items-center gap-1"><Search className="h-3.5 w-3.5" /> Find a school or portfolio</div>
        <div className="flex gap-2">
          <Input placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} data-testid="intel-search-input" />
          <Button onClick={search} disabled={searching} data-testid="intel-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        <div className="mt-3 space-y-1" data-testid="intel-search-results">
          {results.map((r) => { const on = enabledKeys.has(`${r.scope_type}:${r.scope_id}`); const I = Icon(r.scope_type); return (
            <div key={`${r.scope_type}:${r.scope_id}`} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted">
              <div className="flex items-center gap-2 min-w-0">
                <I className="h-4 w-4 text-foreground/40 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{r.name}</div>
                  <div className="text-[11.5px] text-foreground/45 capitalize">{r.scope_type}</div>
                </div>
              </div>
              <Button size="sm" variant={on ? "outline" : "default"} className={on ? "text-red-500" : ""}
                disabled={busy === `${r.scope_type}:${r.scope_id}`} onClick={() => toggle(r.scope_type, r.scope_id, !on)}
                data-testid={`intel-grant-${r.scope_id}`}>
                {busy === `${r.scope_type}:${r.scope_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (on ? "Disable" : "Enable")}
              </Button>
            </div>
          ); })}
          {results.length === 0 && q && !searching && <div className="text-sm text-foreground/40 py-2">No matches.</div>}
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[11.5px] text-foreground/50 bg-muted rounded-lg px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Once enabled, users in that school/portfolio see a "School Intelligence" panel in their portal. Answers are grounded ONLY in data they're permitted to see — never other schools. Tenant/portfolio admins can turn individual users off from within their portal.</span>
      </div>
    </div>
  );
}
